import * as React from "react";
import {
  CellContext,
  createColumnHelper
} from "@tanstack/react-table";

import { Text } from "src/library/components/Text";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { formatPrice } from "src/core/prices/utils/price-helpers";
import {
  Transaction,
  TransactionStatus,
  TransactionType
} from "src/core/accounting/api/accounting.api";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import AccountingStatusBadge from "src/core/accounting/components/AccountingStatusBadge";
import { formatDropName } from "src/core/drops/utils/format-drop-name";

export interface TransactionTableColumns {
  actionButton?: (info: CellContext<Transaction, unknown>) => React.ReactNode;
  showActions?: boolean;
  /** Terminology for dynamic column headers */
  terminology?: ITerminology;
  /** Show matched value column (for invoice-transaction relationship) */
  showMatchedValue?: boolean;
  currency?: string;
}
/**
 * Format transaction type for display (e.g., "credit_note" -> "Credit")
 */
export const formatTransactionType = (type: TransactionType): string => {
  const typeLabels: Record<TransactionType, string> = {
    charge: "Payment",
    payment: "Payment",
    refund: "Credit",
    credit_note: "Credit",
    debit_note: "Debit",
    invoice: "Invoice"
  };

  return typeLabels[ type ] || type.charAt(0).toUpperCase() + type.slice(1);
};

export const transactionTableColumns = (params: TransactionTableColumns) => {
  const customColumnHelper = customCreateColumnHelper<Transaction>();
  const columnHelper = createColumnHelper<Transaction>();

  // Date column (matches Figma design)
  const dateColumn = customColumnHelper.dateColumn(columnHelper, "transactionDate", {
    header: "Date",
    enableSorting: true
  });

  // Amount column
  const amountColumn = customColumnHelper.priceColumn(columnHelper, "amount", {
    header: "Amount",
    cell: ({ getValue }) => {
      const amount = getValue() as number;

      return (
        <Text>
          {formatPrice(amount)}
        </Text>
      );
    }
  }, params.currency ?? "GBP");

  // Type column (Credit / Payment)
  const typeColumn = customColumnHelper.standardTextColumn(columnHelper, "type", {
    id: "type",
    header: "Type",
    enableColumnFilter: true,
    cell: ({ getValue }) => {
      const type = getValue() as TransactionType;

      return (
        <Text>
          {formatTransactionType(type)}
        </Text>
      );
    }
  });

  // Status badge column
  const statusColumn = customColumnHelper.standardTextColumn(columnHelper, "status", {
    header: "Status",
    enableColumnFilter: true,
    cell: ({ getValue }) => {
      const status = getValue() as TransactionStatus;

      return (
        <AccountingStatusBadge
          variant="transaction"
          status={status}
          compact
        />
      );
    }
  });

  const dropColumn = customColumnHelper.standardTextColumn(columnHelper, "branch", {
    header: params.terminology?.drop.singular.text,
    cell: ({ row }) => (
      <Text>
        {row.original.branch ? formatDropName(row.original.branch) : "-"}
      </Text>
    ),
    enableSorting: false
  });

  // Build columns array (matches Figma: Date, Amount, Type, Status, Actions)
  const columns = [
    dateColumn,
    dropColumn,
    amountColumn,
    typeColumn,
    statusColumn
  ];

  // Add matched value column if enabled (for invoice-transaction relationship)
  // Display as positive value for intuitive user experience (payments reduce outstanding balance)
  if (params.showMatchedValue) {
    const matchedValueColumn = customColumnHelper.priceColumn(columnHelper, "matchedValue", {
      id: "matchedValue",
      header: "Value",
      cell: ({ row }) => {
        const matchedValue = row.original.invoices?.[ 0 ].matchedValue;

        if (matchedValue === null || matchedValue === undefined) {
          return (
            <Text>
              -
            </Text>
          );
        }

        // Display as positive value for intuitive UX:
        // Backend stores payments as negative (reducing invoice balance),
        // but users expect to see the payment amount as a positive value
        const displayValue = Math.abs(matchedValue);

        return (
          <Text>
            {formatPrice(displayValue)}
          </Text>
        );
      }
    }, params.currency ?? "GBP");

    columns.push(matchedValueColumn);
  }

  // Add actions column if enabled
  if (params.showActions && params.actionButton) {
    columns.push(customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 150 }));
  }

  return columns;
};
